'use client';

import React, { useEffect, useRef } from 'react';

const reasons = [
  {
    number: '01',
    title: 'Diverse Portfolio',
    description: 'Multiple therapeutic categories under one pharmaceutical portfolio.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="3" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="2"/>
        <rect x="14" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="2"/>
        <rect x="3" y="14" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="2"/>
        <rect x="14" y="14" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="2"/>
      </svg>
    ),
  },
  {
    number: '02',
    title: 'Quality Focus',
    description: 'Quality-oriented approach to pharmaceutical healthcare products.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M12 2L15 9H22L16.5 13.5L18.5 21L12 17L5.5 21L7.5 13.5L2 9H9L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
      </svg>
    ),
  },
  {
    number: '03',
    title: 'Patient-Centric Approach',
    description: 'Products developed and presented around diverse healthcare requirements.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M12 21C12 21 4 15 4 9C4 6.2 6.2 4 9 4C10.7 4 12.1 4.8 12 6C12 4.8 13.3 4 15 4C17.8 4 20 6.2 20 9C20 15 12 21 12 21Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
      </svg>
    ),
  },
  {
    number: '04',
    title: 'Continuous Advancement',
    description: 'Committed to advancing healthcare through a growing product portfolio.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M4 20L8 12L12 16L16 8L20 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M17 8H20V11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  },
];

export default function WhyEwin() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.section-reveal').forEach((el) => el.classList.add('visible'));
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 lg:py-28 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14 space-y-3">
          <div className="section-reveal stagger-1">
            <span className="section-label">Our Strengths</span>
          </div>
          <h2 className="section-reveal stagger-2 font-display text-foreground leading-tight" style={{ fontSize: 'clamp(1.75rem, 3vw, 2.5rem)' }}>
            Why EWIN Lifescience
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {reasons?.map((reason, i) => (
            <div key={reason?.number} className={`value-card group section-reveal stagger-${i + 2}`}>
              <div className="flex items-start justify-between mb-5">
                <div className="w-10 h-10 bg-blue-50 text-primary rounded-lg flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-colors">
                  {reason?.icon}
                </div>
                <span className="font-display text-4xl text-border">{reason?.number}</span>
              </div>
              <h3 className="font-display text-foreground text-lg mb-2 leading-tight">{reason?.title}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">{reason?.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}